import { useState } from 'react';
import { NavLink, Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen,
  ChartColumn,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code2,
  Kanban,
  LayoutGrid,
  ListTodo,
  Mail,
  Settings,
  Shield,
  Users,
} from 'lucide-react';

import type { BoardConfig } from '@tasks/contracts';

import { IconButton } from '../components/IconButton';
import { useHotkeys } from '../hooks/useHotkeys';
import { useProjectBoards } from '../hooks/useBoards';
import { useIsNarrowViewport } from '../hooks/useMediaQuery';
import { useProject } from '../hooks/useProject';
import { useUiStore } from '../stores/uiStore';
import { cn } from '../lib/cn';
import { AppHeader } from './AppHeader';
import { HotkeyHelp } from './HotkeyHelp';
import { QuickCreateIssueModal } from '../features/issue/QuickCreateIssueModal';
import styles from './AppLayout.module.css';

type RailItem = {
  readonly to: string;
  readonly label: string;
  readonly icon: JSX.Element;
  readonly end?: boolean;
};

type BoardsRailGroupProps = {
  readonly base: string;
  readonly boards: readonly BoardConfig[];
  readonly defaultBoardPath: string;
  readonly isCollapsed: boolean;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
};

/**
 * Пункт «Доски» с вложенным списком досок проекта (docs/06 §2).
 *
 * У проекта может быть несколько досок, поэтому пункт раскрывается списком:
 * каждая доска — ссылка `/projects/:key/boards/:id`. Свёрнутый rail не
 * помещает подпункты, поэтому там остаётся только иконка со списком в title.
 */
function BoardsRailGroup({
  base,
  boards,
  defaultBoardPath,
  isCollapsed,
  isOpen,
  onToggle,
}: BoardsRailGroupProps) {
  const navigate = useNavigate();
  const hasMany = boards.length > 1;
  // Пункт верхнего уровня подсвечивается, когда открыта любая доска проекта:
  // иначе на доске не подсвечен ни один пункт rail (регрессия после «Доски»).
  const isBoardActive = useMatch('/projects/:key/boards/:boardId') !== null;

  if (isCollapsed) {
    return (
      <NavLink
        to={defaultBoardPath}
        title={`Доски: ${boards.map((board) => board.name).join(', ') || 'нет досок'}`}
        className={cn(styles['railLink'], isBoardActive && styles['railLinkActive'])}
      >
        <span aria-hidden className={styles['railIcon']}>
          <Kanban size={18} />
        </span>
      </NavLink>
    );
  }

  return (
    <div className={styles['railGroup']}>
      <button
        type="button"
        className={cn(
          styles['railLink'],
          styles['railGroupToggle'],
          isBoardActive && (hasMany ? styles['railGroupToggleActive'] : styles['railLinkActive']),
        )}
        aria-expanded={hasMany ? isOpen : undefined}
        onClick={() => {
          if (hasMany) {
            onToggle();
            return;
          }
          // Одна доска: раскрывать нечего — пункт сразу открывает её.
          navigate(defaultBoardPath);
        }}
      >
        <span aria-hidden className={styles['railIcon']}>
          <Kanban size={18} />
        </span>
        <span>Доски</span>
        {hasMany ? (
          <span aria-hidden className={cn(styles['railChevron'], isOpen && styles['railChevronOpen'])}>
            <ChevronDown size={14} />
          </span>
        ) : null}
      </button>

      {hasMany && isOpen ? (
        <ul className={styles['railSubList']} aria-label="Доски проекта">
          {boards.map((board) => (
            <li key={board.id}>
              <NavLink
                to={`${base}/boards/${board.id}`}
                title={board.name}
                className={({ isActive }) =>
                  cn(styles['railSubLink'], isActive && styles['railSubLinkActive'])
                }
              >
                <span className="u-truncate">{board.name}</span>
                {board.isDefault ? <span className={styles['railSubBadge']}>по умолчанию</span> : null}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Header + боковой rail (docs/06 §2). Rail сворачивается при < 1280 px. */
export function AppLayout() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { project } = useProject(key);
  const isNarrow = useIsNarrowViewport();
  const isRailCollapsed = useUiStore((state) => state.isRailCollapsed) || isNarrow;
  const toggleRail = useUiStore((state) => state.toggleRail);

  const base = `/projects/${key ?? ''}`;
  const isScrum = project?.boardType === 'scrum';
  const isAdmin = project?.myRole === 'project_admin';
  const { boards, defaultBoard } = useProjectBoards(key);
  const [isBoardsOpen, setBoardsOpen] = useState(true);

  // Доска по умолчанию: по ней открываются «Доски» и хоткей B (docs/06 §6).
  const defaultBoardPath = defaultBoard ? `${base}/boards/${defaultBoard.id}` : `${base}/board`;

  // Первый пункт rail — «Доски»: раскрывается списком досок проекта.
  const railItems: readonly RailItem[] = [
    ...(isScrum ? [{ to: `${base}/backlog`, label: 'Бэклог', icon: <ListTodo size={18} /> }] : []),
    { to: `${base}/reports`, label: 'Отчёты', icon: <ChartColumn size={18} /> },
    { to: `${base}/wiki`, label: 'Wiki', icon: <BookOpen size={18} /> },
  ];

  // Навигация: B — доска проекта, G — бэклог, M — моя доска (docs/06 §6).
  useHotkeys({
    b: () => {
      if (key) {
        navigate(defaultBoardPath);
      }
    },
    g: () => {
      if (key && project?.boardType === 'scrum') {
        navigate(`/projects/${key}/backlog`);
      }
    },
    m: () => navigate('/my'),
  });

  const settingsItems: readonly RailItem[] = [
    { to: `${base}/settings/general`, label: 'Основное', icon: <Settings size={18} /> },
    { to: `${base}/settings/workflow`, label: 'Workflow', icon: <LayoutGrid size={18} /> },
    { to: `${base}/settings/members`, label: 'Участники', icon: <Users size={18} /> },
    { to: `${base}/settings/danger`, label: 'Опасная зона', icon: <Shield size={18} /> },
  ];

  return (
    <div className={styles['shell']}>
      <AppHeader project={project} />
      <div className={styles['body']}>
        {key ? (
          <nav
            className={cn(styles['rail'], isRailCollapsed && styles['railCollapsed'])}
            aria-label="Навигация по проекту"
          >
            <ul className={styles['railList']}>
              <li>
                <BoardsRailGroup
                  base={base}
                  boards={boards}
                  defaultBoardPath={defaultBoardPath}
                  isCollapsed={isRailCollapsed}
                  isOpen={isBoardsOpen}
                  onToggle={() => setBoardsOpen((open) => !open)}
                />
              </li>
              {railItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    title={item.label}
                    className={({ isActive }) => cn(styles['railLink'], isActive && styles['railLinkActive'])}
                  >
                    <span aria-hidden className={styles['railIcon']}>
                      {item.icon}
                    </span>
                    {isRailCollapsed ? null : <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>

            {isAdmin ? (
              <div className={styles['railSection']}>
                {isRailCollapsed ? null : <p className={styles['railHeading']}>Настройки</p>}
                <ul className={styles['railList']}>
                  {settingsItems.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        title={item.label}
                        className={({ isActive }) => cn(styles['railLink'], isActive && styles['railLinkActive'])}
                      >
                        <span aria-hidden className={styles['railIcon']}>
                          {item.icon}
                        </span>
                        {isRailCollapsed ? null : <span>{item.label}</span>}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {isNarrow ? null : (
              <div className={styles['railFooter']}>
                <IconButton
                  label={isRailCollapsed ? 'Развернуть панель' : 'Свернуть панель'}
                  icon={isRailCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                  onClick={toggleRail}
                />
              </div>
            )}
          </nav>
        ) : null}

        <main className={styles['content']}>
          <Outlet />
        </main>
      </div>
      <HotkeyHelp />
      <QuickCreateIssueModal />
    </div>
  );
}

/** Заглушка для admin-разделов вне проекта. */
export function AdminLayout() {
  const isRailCollapsed = useUiStore((state) => state.isRailCollapsed);
  const isNarrow = useIsNarrowViewport();
  const collapsed = isRailCollapsed || isNarrow;

  return (
    <div className={styles['shell']}>
      <AppHeader project={null} />
      <div className={styles['body']}>
        <nav className={cn(styles['rail'], collapsed && styles['railCollapsed'])} aria-label="Администрирование">
          <ul className={styles['railList']}>
            <li>
              <NavLink to="/" end className={styles['railLink']}>
                <span aria-hidden className={styles['railIcon']}>
                  <LayoutGrid size={18} />
                </span>
                {collapsed ? null : <span>Проекты</span>}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/users"
                className={({ isActive }) => cn(styles['railLink'], isActive && styles['railLinkActive'])}
              >
                <span aria-hidden className={styles['railIcon']}>
                  <Users size={18} />
                </span>
                {collapsed ? null : <span>Пользователи</span>}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/mail"
                className={({ isActive }) => cn(styles['railLink'], isActive && styles['railLinkActive'])}
              >
                <span aria-hidden className={styles['railIcon']}>
                  <Mail size={18} />
                </span>
                {collapsed ? null : <span>Почта</span>}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/audit"
                className={({ isActive }) => cn(styles['railLink'], isActive && styles['railLinkActive'])}
              >
                <span aria-hidden className={styles['railIcon']}>
                  <Shield size={18} />
                </span>
                {collapsed ? null : <span>Аудит</span>}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/api"
                className={({ isActive }) => cn(styles['railLink'], isActive && styles['railLinkActive'])}
              >
                <span aria-hidden className={styles['railIcon']}>
                  <Code2 size={18} />
                </span>
                {collapsed ? null : <span>API</span>}
              </NavLink>
            </li>
          </ul>
        </nav>
        <main className={styles['content']}>
          <Outlet />
        </main>
      </div>
      <HotkeyHelp />
    </div>
  );
}

export type { RailItem };
